import {
  FileImageOutlined,
  FilePdfOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  byMonth,
  describeError,
  dirhams,
  findUuid,
  fullDate,
  monthYear,
  operationsCount,
  present,
  type ResultOf,
  TYPE_LABELS,
} from '@horof/shared';
import { Alert, Button, Empty, Image, Input, Skeleton } from 'antd';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'urql';
import {
  type OperationRowFragment,
  OperationsDocument,
  withBalances,
} from './finance-data';

const PAGE = 50;

/**
 * All operations, newest first, by month; a search in the description, or
 * a pasted operation id.
 */
export function OperationsPage() {
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const q = search.trim();
  // A pasted id (doc 56): that operation alone.
  const id = findUuid(q);
  const [{ data, error, fetching }] = useQuery({
    query: OperationsDocument,
    variables: {
      filter: id
        ? { id: { equalTo: id } }
        : q
          ? { description: { includesInsensitive: q } }
          : null,
      orderBy: ['DATE_DESC', 'CREATED_AT_DESC'],
      first: limit,
    },
    context: useMemo(() => withBalances, []),
  });
  const operations = present(data?.financialOperations?.nodes);
  const total = data?.financialOperations?.totalCount ?? 0;

  return (
    <div className="page">
      <h1 className="member-title">العمليات</h1>
      <Input
        size="large"
        allowClear
        prefix={<SearchOutlined />}
        placeholder="بحث في البيان، أو لصق معرّف"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setLimit(PAGE);
        }}
      />
      {error && (
        <Alert
          type="error"
          showIcon
          message={describeError(error, 'تعذّر تحميل العمليات.')}
        />
      )}
      {!data && !error && <Skeleton active paragraph={{ rows: 8 }} />}
      {data && <div className="field-hint">{operationsCount(total)}</div>}
      {data && operations.length === 0 && <Empty description="لا عمليات" />}
      <OperationGroups operations={operations} />
      {operations.length < total && (
        <Button
          size="large"
          loading={fetching}
          onClick={() => setLimit((l) => l + PAGE)}
        >
          عرض المزيد ({total - operations.length})
        </Button>
      )}
    </div>
  );
}

export type RowOperation = ResultOf<typeof OperationRowFragment>;

export function OperationGroups({
  operations,
}: {
  operations: RowOperation[];
}) {
  return byMonth(operations).map((group) => (
    <section key={group.month} className="month-group">
      <h2 className="month-group-title">{monthYear(`${group.month}-15`)}</h2>
      <ul className="operation-list">
        {group.rows.map((op) => (
          <li key={op.id}>
            <OperationRow op={op} />
          </li>
        ))}
      </ul>
    </section>
  ));
}

/** What, when, for which project and from which account, and the amount. */
export function OperationRow({ op }: { op: RowOperation }) {
  return (
    <Link to={`/operations/${op.id}`} className={`operation-row is-${op.type}`}>
      <span className="operation-main">
        <span className="operation-description">
          {op.description || op.category?.name || TYPE_LABELS[op.type]}
        </span>
        <span className="field-hint">
          {[fullDate(op.date), op.project?.name, op.account?.name]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      {op.receiptPhoto && <ReceiptIcon photo={op.receiptPhoto} />}
      <span className="operation-amount">
        {op.type === 'credit' ? '+' : '−'} {dirhams(op.amount)}
      </span>
    </Link>
  );
}

/**
 * The receipt of an operation: tapping the icon shows the photo full screen
 * (or opens the PDF) without opening the operation.
 */
function ReceiptIcon({
  photo,
}: {
  photo: { url: string; contentType: string };
}) {
  const [open, setOpen] = useState(false);
  const pdf = photo.contentType === 'application/pdf';
  return (
    <>
      <button
        type="button"
        className="receipt-icon"
        aria-label="صورة الوصل"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (pdf) window.open(photo.url, '_blank', 'noopener');
          else setOpen(true);
        }}
      >
        {pdf ? <FilePdfOutlined /> : <FileImageOutlined />}
      </button>
      {!pdf && (
        <Image
          src={photo.url}
          alt="صورة الوصل"
          style={{ display: 'none' }}
          preview={{ visible: open, onVisibleChange: setOpen, src: photo.url }}
        />
      )}
    </>
  );
}
